import { Trans } from "@lingui/macro";
import keys from "lodash/keys";
import uniq from "lodash/uniq";
import { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";

import { ARBITRUM, BSC } from "config/chains";
import { getIcon } from "config/icons";
import { getIncentivesV2Url } from "config/links";
import { getMarketListingDate } from "config/markets";
import useIncentiveStats from "domain/synthetics/common/useIncentiveStats";
import { getIsBaseApyReadyToBeShown } from "domain/synthetics/markets/getIsBaseApyReadyToBeShown";
import type { MarketTokensAPRData } from "domain/synthetics/markets/types";
import { useGmMarketsApy } from "domain/synthetics/markets/useGmMarketsApy";
import { useChainId } from "lib/chains";
import { isHomeSite } from "lib/legacy";
import { formatAmount } from "lib/numbers";
import { switchNetwork } from "lib/wallets";
import useWallet from "lib/wallets/useWallet";

import BannerButton from "components/Banner/BannerButton";
import Button from "components/Button/Button";
import ExternalLink from "components/ExternalLink/ExternalLink";
import APRLabel from "../APRLabel/APRLabel";
import { HeaderLink } from "../Header/HeaderLink";

import sparkleIcon from "img/sparkle.svg";
import { GlvList, useGlvMarketsInfo } from "domain/synthetics/markets/useGlvMarkets";
import { isGlvEnabled } from "domain/synthetics/markets/glv";
import { RawIncentivesStats } from "lib/oracleKeeperFetcher";
import { TrackingLink } from "components/TrackingLink/TrackingLink";
import { userAnalytics } from "lib/userAnalytics";
import { LandingPageProtocolReadMoreEvent, LandingPageProtocolTokenEvent } from "lib/userAnalytics/types";

const mlpIcon = getIcon("common", "mlp");
const moneyIcon = getIcon("common", "money");
const gmIcon = getIcon("common", "gm");
const glvIcon = getIcon("common", "glv");

function calculateMaxApr(apr: MarketTokensAPRData, incentiveApr: MarketTokensAPRData, chainId?: number) {
  const allKeys = uniq(keys(apr).concat(keys(incentiveApr)));

  let maxApr = 0n;

  for (const key of allKeys) {
    const isBaseApyReadyToBeShown = chainId ? getIsBaseApyReadyToBeShown(getMarketListingDate(chainId, key)) : true;

    let aprValue = 0n;
    if (isBaseApyReadyToBeShown) {
      aprValue = apr[key] ?? 0n;
    }

    const incentiveAprValue = incentiveApr[key] ?? 0n;
    const totalApr = aprValue + incentiveAprValue;

    if (totalApr > maxApr) {
      maxApr = totalApr;
    }
  }

  return maxApr;
}

const hasGlvRewards = (stats: RawIncentivesStats | null, glvs: GlvList | undefined) =>
  stats?.lp?.isActive &&
  Object.entries(stats?.lp.rewardsPerMarket ?? {}).some(([market, reward]) => {
    return Object.values(glvs ?? {}).some(({ glv }) => glv.glvToken === market && BigInt(reward) > 0n);
  });

type Props = {
  showRedirectModal?: (to: string) => void;
};

const BuyLink = ({
  chainId,
  active,
  className,
  to,
  children,
  network,
  showRedirectModal,
  trackLinkHandler,
}: {
  chainId: number;
  active: boolean;
  className: string;
  to: string;
  children: React.ReactNode;
  network: number;
  showRedirectModal: undefined | ((to: string) => void);
  trackLinkHandler?: () => void | Promise<void>;
}) => {
  const changeNetwork = useCallback(
    (network) => {
      if (network === chainId) {
        return;
      }
      if (!active) {
        // wait until the internal navigation is done
        setTimeout(() => {
          return switchNetwork(network, active);
        }, 500);
      } else {
        return switchNetwork(network, active);
      }
    },
    [chainId, active]
  );

  const isHome = isHomeSite();
  if (isHome && showRedirectModal) {
    return (
      <HeaderLink onClick={trackLinkHandler} to={to} className={className} showRedirectModal={showRedirectModal}>
        {children}
      </HeaderLink>
    );
  }

  const LinkComponent = (
    <Link to={to} className={className} onClick={() => changeNetwork(network)}>
      {children}
    </Link>
  );

  return trackLinkHandler && isHome ? (
    <TrackingLink onClick={trackLinkHandler}>{LinkComponent}</TrackingLink>
  ) : (
    LinkComponent
  );
};

async function sendUserAnalyticsProtocolTokenEvent(
  chain: "Ronin" | "Bsc",
  type: "MONEY" | "GM" | "GLV" | "MLP"
) {
  await userAnalytics.pushEvent<LandingPageProtocolTokenEvent>(
    {
      event: "LandingPageAction",
      data: {
        action: "ProtocolTokenAction",
        chain,
        type,
      },
    },
    { instantSend: true }
  );
}

const trackMONEYBuyArbitrum = () => sendUserAnalyticsProtocolTokenEvent("Ronin", "MONEY");
const trackMONEYBuyBsc = () => sendUserAnalyticsProtocolTokenEvent("Bsc", "MONEY");
const trackMLPBuyArbitrum = () => sendUserAnalyticsProtocolTokenEvent("Ronin", "MLP");
const trackMLPBuyBsc = () => sendUserAnalyticsProtocolTokenEvent("Bsc", "MLP");
const trackGLVBuyArbitrum = () => sendUserAnalyticsProtocolTokenEvent("Ronin", "GLV");
const trackGLVBuyBsc = () => sendUserAnalyticsProtocolTokenEvent("Bsc", "GLV");
const trackGMBuyArbitrum = () => sendUserAnalyticsProtocolTokenEvent("Ronin", "GM");
const trackGMBuyBsc = () => sendUserAnalyticsProtocolTokenEvent("Bsc", "GM");

function getTrackingLink(link: string) {
  if (!isHomeSite()) {
    return link;
  }

  const paramsPrefix = link.includes("?") ? "&" : "?";

  return `${link}${paramsPrefix}${userAnalytics.getSessionIdUrlParams()}`;
}

async function sendUserAnalyticsProtocolReadMoreEvent() {
  if (isHomeSite()) {
    await userAnalytics.pushEvent<LandingPageProtocolReadMoreEvent>(
      { event: "LandingPageAction", data: { action: "ProtocolReadMore" } },
      { instantSend: true }
    );
  }
}

export default function TokenCard({ showRedirectModal }: Props) {
  const { chainId } = useChainId();
  const { active, account } = useWallet();
  const arbitrumIncentiveState = useIncentiveStats(ARBITRUM);
  // const bscIncentiveState = useIncentiveStats(BSC);

  // const { glvs: glvArb } = useGlvMarketsInfo(isGlvEnabled(ARBITRUM), {
  //   chainId: ARBITRUM,
  //   account,
  //   marketsInfoData: undefined,
  //   tokensData: undefined,
  //   filterIncorrectMarkets: false,
  // });

  // const { glvs: glvRon } = useGlvMarketsInfo(isGlvEnabled(BSC), {
  //   chainId: BSC,
  //   marketsInfoData: undefined,
  //   tokensData: undefined,
  //   account,
  //   filterIncorrectMarkets: false,
  // });

  // const {
  //   marketsTokensApyData: arbApy,
  //   marketsTokensIncentiveAprData: arbIncentiveApr,
  //   glvTokensIncentiveAprData: arbGlvIncentiveApr,
  //   glvApyInfoData: arbGlvApy,
  // } = useGmMarketsApy(ARBITRUM);
  // const {
  //   marketsTokensApyData: ronApy,
  //   marketsTokensIncentiveAprData: ronIncentiveApr,
  //   glvTokensIncentiveAprData: ronGlvIncentiveApr,
  //   glvApyInfoData: ronGlvApy,
  // } = useGmMarketsApy(BSC);

  // const maxMarketApyText = useMemo(() => {
  //   if (!arbApy || !arbIncentiveApr || !ronApy || !ronIncentiveApr)
  //     return {
  //       [ARBITRUM]: "...%",
  //       [BSC]: "...%",
  //     };

  //   const maxArbApy = calculateMaxApr(arbApy, arbIncentiveApr, ARBITRUM);
  //   const maxRonApy = calculateMaxApr(ronApy, ronIncentiveApr, BSC);

  //   return {
  //     [ARBITRUM]: `${formatAmount(maxArbApy, 28, 2)}%`,
  //     [BSC]: `${formatAmount(maxRonApy, 28, 2)}%`,
  //   };
  // }, [arbApy, arbIncentiveApr, ronApy, ronIncentiveApr]);

  // const maxGlvApyText = useMemo(() => {
  //   const arb = !arbGlvApy
  //     ? "...%"
  //     : `${formatAmount(calculateMaxApr(arbGlvApy, arbGlvIncentiveApr ?? {}, ARBITRUM), 28, 2)}%`;
  //   const ron = !ronGlvApy
  //     ? "...%"
  //     : `${formatAmount(calculateMaxApr(ronGlvApy, ronGlvIncentiveApr ?? {}, BSC), 28, 2)}%`;
  //   return {
  //     [ARBITRUM]: isGlvEnabled(ARBITRUM) ? arb : undefined,
  //     [BSC]: isGlvEnabled(BSC) ? ron : undefined,
  //   };
  // }, [arbGlvApy, ronGlvApy, arbGlvIncentiveApr, ronGlvIncentiveApr]);

  // const poolsIncentivizedLabel = useMemo(() => {
  //   const sparkle = <img src={sparkleIcon} alt="sparkle" className="relative -top-2 -mr-10 inline h-10 align-top" />;
  //   const bscLink = <ExternalLink href={getIncentivesV2Url(BSC)}>Bsc</ExternalLink>;

  //   if (bscIncentiveState?.lp?.isActive) {
  //     return (
  //       <Trans>
  //         {bscLink} GM Pools are <span className="whitespace-nowrap">incentivized{sparkle}.</span>
  //       </Trans>
  //     );
  //   } else {
  //     return null;
  //   }
  // }, [bscIncentiveState?.lp?.isActive]);

  // const glvsIncentivizedLabel = useMemo(() => {
  //   const sparkle = <img src={sparkleIcon} alt="sparkle" className="relative -top-2 -mr-10 inline h-10 align-top" />;
  //   const arbitrumLink = <ExternalLink href={getIncentivesV2Url(ARBITRUM)}>Ronin</ExternalLink>;
  //   const bscLink = <ExternalLink href={getIncentivesV2Url(BSC)}>Bsc</ExternalLink>;

  //   const hasArbitrumGlvIncentives = hasGlvRewards(arbitrumIncentiveState, glvArb);
  //   const hasRonGlvIncentives = hasGlvRewards(bscIncentiveState, glvRon);

  //   if (hasArbitrumGlvIncentives && hasRonGlvIncentives) {
  //     return (
  //       <Trans>
  //         {arbitrumLink} and {bscLink} GLV Pools are{" "}
  //         <span className="whitespace-nowrap">incentivized{sparkle}.</span>
  //       </Trans>
  //     );
  //   } else if (hasArbitrumGlvIncentives) {
  //     return (
  //       <Trans>
  //         {arbitrumLink} GLV Pools are <span className="whitespace-nowrap">incentivized{sparkle}.</span>
  //       </Trans>
  //     );
  //   } else if (hasRonGlvIncentives) {
  //     return (
  //       <Trans>
  //         {bscLink} GLV Pools are <span className="whitespace-nowrap">incentivized{sparkle}.</span>
  //       </Trans>
  //     );
  //   } else {
  //     return null;
  //   }
  // }, [glvArb, glvRon, arbitrumIncentiveState, bscIncentiveState]);

  return (
    <div className="Home-token-card-options">
      <div className="Home-token-card-option">
        <div>
          <div className="Home-token-card-option-icon">
            <img src={moneyIcon} width="40" alt="MONEY Icons" /> MONEY
          </div>
          <div className="Home-token-card-option-info">
            <div className="Home-token-card-option-title">
              <Trans>
                MONEY is the utility and governance token. Accrues 30% markets generated fees, respectively.
              </Trans>
            </div>
            <div className="Home-token-card-option-apr">
              {/* <Trans>Ronin APR:</Trans> <APRLabel chainId={ARBITRUM} label="avgMONEYAprTotal" />,{" "} */}
              <Trans>Bsc APR:</Trans> <APRLabel chainId={BSC} label="avgMONEYAprTotal" />
            </div>
          </div>
        </div>
        <div className="Home-token-card-option-action">
          <div className="buy">
            {/* <BuyLink
              chainId={chainId}
              active={active}
              className="default-btn"
              to={getTrackingLink("/buy_money")}
              network={ARBITRUM}
              showRedirectModal={showRedirectModal}
              trackLinkHandler={trackMONEYBuyArbitrum}
            >
              <Trans>View on Ronin</Trans>
            </BuyLink> */}
            <BuyLink
              chainId={chainId}
              active={active}
              className="default-btn"
              to={getTrackingLink("/buy_money")}
              network={BSC}
              showRedirectModal={showRedirectModal}
              trackLinkHandler={trackMONEYBuyBsc}
            >
              <Trans>View on BSC</Trans>
            </BuyLink>
          </div>
          <TrackingLink onClick={sendUserAnalyticsProtocolReadMoreEvent}>
            <Button
              className="!py-11 tracking-normal"
              newTab
              variant="primary"
              to="https://moneyx.nuclino-site.com"
            >
              <Trans>Read more</Trans>
            </Button>
          </TrackingLink>
        </div>
      </div>
      {/* <div className="Home-token-card-option">
        <div>
          <div className="Home-token-card-option-icon">
            <img src={glvIcon} alt="GLV icon" /> GLV
          </div>
          <div className="Home-token-card-option-info">
            <div className="Home-token-card-option-title">
              <Trans>
                GLV is the liquidity provider token for MONEY V2 vaults. Consist of several GM tokens and accrues fees
                generated by them.
              </Trans>
            </div>
          </div>
          {glvsIncentivizedLabel && (
            <div className="text-body-large mt-15 rounded-4 bg-cold-blue-900 px-15 py-8">{glvsIncentivizedLabel}</div>
          )}
          <div className="Home-token-card-option-apr">
            <Trans>Ronin Max. APY:</Trans> {maxGlvApyText?.[ARBITRUM]}
            {maxGlvApyText?.[BSC] && (
              <>
                , <Trans>Bsc Max. APY: {maxGlvApyText?.[BSC]}</Trans>
              </>
            )}
          </div>
        </div>

        <div className="Home-token-card-option-action Token-card-buy">
          <div className="buy">
            <BuyLink
              to={getTrackingLink("/pools?pickBestGlv=1")}
              className="default-btn"
              network={ARBITRUM}
              chainId={chainId}
              active={active}
              showRedirectModal={showRedirectModal}
              trackLinkHandler={trackGLVBuyArbitrum}
            >
              <Trans>View on Ronin</Trans>
            </BuyLink>
            {isGlvEnabled(BSC) && (
              <BuyLink
                to={getTrackingLink("/pools?pickBestGlv=1")}
                className="default-btn"
                network={BSC}
                chainId={chainId}
                active={active}
                showRedirectModal={showRedirectModal}
                trackLinkHandler={trackGLVBuyBsc}
              >
                <Trans>View on BSC</Trans>
              </BuyLink>
            )}
          </div>
          <TrackingLink onClick={sendUserAnalyticsProtocolReadMoreEvent}>
            <a
              href="https://moneyx.nuclino-site.com"
              target="_blank"
              rel="noreferrer"
              className="default-btn read-more"
            >
              <Trans>Read more</Trans>
            </a>
          </TrackingLink>
        </div>
      </div> */}
      {/* <div className="Home-token-card-option">
        <div>
          <div className="Home-token-card-option-icon">
            <img src={gmIcon} alt="moneyBigIcon" /> GM
          </div>
          <div className="Home-token-card-option-info">
            <div className="Home-token-card-option-title">
              <Trans>
                GM is the liquidity provider token for MONEY V2 markets. Accrues 63% of the V2 markets generated fees.
              </Trans>
            </div>
          </div>
          {poolsIncentivizedLabel && (
            <div className="text-body-large mt-15 rounded-4 bg-cold-blue-900 px-15 py-8">{poolsIncentivizedLabel}</div>
          )}
          <div className="Home-token-card-option-apr">
            <Trans>Ronin Max. APY:</Trans> {maxMarketApyText?.[ARBITRUM]},{" "}
            <Trans>Bsc Max. APY: {maxMarketApyText?.[BSC]}</Trans>{" "}
          </div>
        </div>

        <div className="Home-token-card-option-action Token-card-buy">
          <div className="buy">
            <BuyLink
              to={getTrackingLink("/pools")}
              className="default-btn"
              network={ARBITRUM}
              chainId={chainId}
              active={active}
              showRedirectModal={showRedirectModal}
              trackLinkHandler={trackGMBuyArbitrum}
            >
              <Trans>View on Ronin</Trans>
            </BuyLink>

            <BuyLink
              to={getTrackingLink("/pools")}
              className="default-btn"
              network={BSC}
              chainId={chainId}
              active={active}
              showRedirectModal={showRedirectModal}
              trackLinkHandler={trackGMBuyBsc}
            >
              <Trans>View on BSC</Trans>
            </BuyLink>
          </div>
          <TrackingLink onClick={sendUserAnalyticsProtocolReadMoreEvent}>
            <a
              href="https://moneyx.nuclino-site.com"
              target="_blank"
              rel="noreferrer"
              className="default-btn read-more"
            >
              <Trans>Read more</Trans>
            </a>
          </TrackingLink>
        </div>
      </div> */}
      <div className="Home-token-card-option">
        <div>
          <div className="Home-token-card-option-icon">
            <img src={mlpIcon} width="40" alt="MLP Icon" /> MLP
          </div>
          <div className="Home-token-card-option-info">
            <div className="Home-token-card-option-title">
              <Trans>
                MLP is the liquidity provider token for MONEY markets. Accrues 70% of the markets generated fees.
              </Trans>
              {arbitrumIncentiveState?.migration?.isActive && (
                <BannerButton
                  className="mt-15"
                  label="Migrating from MLP to GM is incentivized in Ronin."
                  link={getIncentivesV2Url(ARBITRUM)}
                />
              )}
            </div>
            <div className="Home-token-card-option-apr">
              {/* <Trans>Ronin APR:</Trans> <APRLabel chainId={ARBITRUM} label="mlpAprTotal" key="ARBITRUM" />,{" "} */}
              <Trans>Bsc APR:</Trans> <APRLabel chainId={BSC} label="mlpAprTotal" key="BSC" />
            </div>
          </div>
        </div>
        <div className="Home-token-card-option-action">
          <div className="buy">
            {/* <BuyLink
              to={getTrackingLink("/buy_mlp")}
              className="default-btn"
              network={ARBITRUM}
              chainId={chainId}
              active={active}
              showRedirectModal={showRedirectModal}
              trackLinkHandler={trackMLPBuyArbitrum}
            >
              <Trans>View on Ronin</Trans>
            </BuyLink> */}
            <BuyLink
              to={getTrackingLink("/buy_mlp")}
              className="default-btn"
              network={BSC}
              chainId={chainId}
              active={active}
              showRedirectModal={showRedirectModal}
              trackLinkHandler={trackMLPBuyBsc}
            >
              <Trans>View on BSC</Trans>
            </BuyLink>
          </div>
          <TrackingLink onClick={sendUserAnalyticsProtocolReadMoreEvent}>
            <a
              href="https://moneyx.nuclino-site.com"
              target="_blank"
              rel="noreferrer"
              className="default-btn read-more"
            >
              <Trans>Read more</Trans>
            </a>
          </TrackingLink>
        </div>
      </div>
    </div>
  );
}
